import { Quotable, QuoteLine, QuoteUsed } from '@kalos/kalos-rpc';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { default as IconButton } from '@mui/material/IconButton';
import { useQueryClient } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { type FC, useCallback, useEffect, useMemo, useState } from 'react';

import { queryKeys } from '../../../hooks/react-query/constants';
import { useEventQuotesQuery } from '../../../hooks/react-query/useEventQuotes';
import { useQuoteLineQuery } from '../../../hooks/react-query/useQuoteLineQuery';
import {
  makeFakeRows,
  QuoteLineClientService,
  QuoteUsedClientService,
  usd,
} from '../../../tools/helpers';
import { type ActionsProps } from '../Actions';
import { Field, type Value } from '../Field';
import { Form, type Schema } from '../Form';
import { type Columns, type Data, InfoTable } from '../InfoTable';
import { Modal } from '../Modal';
import { SectionBar } from '../SectionBar';
import { Tooltip } from '../Tooltip';
import { EnhancedFilter } from './enhancedFilter';

export type SelectedQuote = {
  quotePart: Quotable;
  billable: boolean;
  quantity: number;
};
interface Props {
  servicesRenderedId?: number;
  onUpdate?: () => Promise<void>;
  savePendingQuotes?: (servicesRenderedId: number, selectedQuotes: Quotable[]) => void;
  pendingQuotableProp?: Quotable[];
  pendingNewQuotableProp?: Quotable[];
  onClose?: () => void;
  archived?: boolean;
  setPendingQuotableProp?: (quotes: Quotable[]) => void;
  setPendingNewQuotableProp?: (quotes: Quotable[]) => void;
  initialDepartment?: number;
}

const COLUMNS: Columns = [
  { name: 'Selected' },
  { name: 'Billable' },
  { name: 'Excl. Discount' },
  { name: 'Part/Labor' },
  { name: 'Quantity' },
  { name: 'Price' },
];

const COLUMNS_QUOTABLE: Columns = [
  { name: 'Description', align: 'center' },
  { name: 'Quantity', align: 'center' },
  { name: 'Price', align: 'center' },
  { name: 'Total Price', align: 'center' },
  { name: 'Billable', align: 'center' },
  { name: 'Excluded', align: 'center' },
  { name: 'Actions', align: 'center' },
];

// Function to create schema based on whether it's a catalog item or custom item
const createQuotableSchema = (quotable?: Quotable): Schema<Quotable> => {
  const isCatalogItem = quotable && quotable.quoteLineId && quotable.quoteLineId !== 0;

  return [
    [
      {
        name: 'description',
        label: 'Item/Labor Name',
        type: 'text',
        multiline: true,
        required: true,
        disabled: !!isCatalogItem, // Disable description editing for catalog items
        helperText: isCatalogItem ? 'Cannot edit description for catalog items' : undefined,
        validationOnSave(value) {
          if (!isCatalogItem && !value.trim().replaceAll(' ', '')) return 'This field is required';
          if (value.length > 1000) return 'The name cannot exceed 1000 characters';
          return '';
        },
      },
    ],

    [
      {
        name: 'isLmpc',
        type: 'checkbox',
        label: 'LMPC',
      },
      {
        name: 'isBillable',
        type: 'checkbox',
        label: 'Billable?',
      },
      {
        name: 'excludeInvoiceDiscount',
        type: 'checkbox',
        label: 'Excl. Invoice Discount',
      },
      {
        name: 'quotedPrice',
        type: 'number',
        label: 'Price',
        required: true,
        startAdornment: '$',
        validationOnSave(value) {
          if (!value.trim().replaceAll(' ', '')) return 'This field is required';
          if (Number(value) < 0) return "This field can't be negative";
          return '';
        },
      },
      {
        name: 'quantity',
        type: 'number',
        label: 'QTY',
        required: true,
        validationOnSave(value) {
          if (!value.trim().replaceAll(' ', '')) return 'This field is required';
          if (Number(value) <= 0) return "This field can't be negative";
          return '';
        },
      },
      {
        name: 'quoteUsedId',
        type: 'hidden',
      },
      {
        name: 'quoteLineId',
        type: 'hidden',
      },
    ],
  ];
};

export const QuoteSelector: FC<Props> = ({
  servicesRenderedId,
  onUpdate,
  pendingNewQuotableProp,
  pendingQuotableProp,
  archived,
  setPendingNewQuotableProp,
  setPendingQuotableProp,
  onClose,
  initialDepartment,
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const [newQuotable, setNewQuotable] = useState<boolean>(false);
  const [filter, setFilter] = useState<string>('');
  const [departmentFilter, setDepartmentFilter] = useState<number[]>(
    initialDepartment ? [initialDepartment] : [],
  );
  const [quotable, setQuotable] = useState<Quotable[]>([]);
  const [pendingQuotable, setPendingQuotable] = useState<Quotable[]>(
    pendingQuotableProp ? pendingQuotableProp : [],
  );
  const [pendingNewQuotable, setPendingNewQuotable] = useState<Quotable[]>(
    pendingNewQuotableProp ? pendingNewQuotableProp : [],
  );
  const [pendingDeleteQuotable, setPendingDeleteQuotable] = useState<Quotable[]>([]);

  const [selectedQuoteLineIds, setSelectedQuoteLineIds] = useState<number[]>([]);
  const [pendingUpdateExistingQuotable, setPendingUpdateExistingQuotable] = useState<Quotable[]>(
    [],
  );
  const [editingQuote, setEditingQuote] = useState<Quotable | undefined>(undefined);
  const [editPendingQuote, setEditPendingQuote] = useState<Quotable | undefined>(undefined);
  const [editPendingNewQuote, setEditPendingNewQuote] = useState<Quotable | undefined>(undefined);
  const [billable, setBillable] = useState<{
    [key: number]: {
      billable: boolean;
      lmpc: boolean;
      quantity: number;
    };
  }>({});

  const [isSelected, setIsSelected] = useState<boolean>(false);
  const [isSelectedValid, setIsSelectedValid] = useState<boolean>(true);

  const isEventQuotesQueryEnabled = !!servicesRenderedId;

  const serviceRenderedQuotesQuery = useEventQuotesQuery({
    filter: {
      servicesRenderedId,
    },
    enabled: isEventQuotesQueryEnabled,
  });

  useEffect(() => {
    if (serviceRenderedQuotesQuery.isSuccess) {
      setQuotable(serviceRenderedQuotesQuery.data?.data || []);
    }
  }, [serviceRenderedQuotesQuery.data?.data, serviceRenderedQuotesQuery.isSuccess]);

  const quotablePartsQuery = useQuoteLineQuery({
    filters: {
      isFlatrate: '1',
      isActive: 1,
      withoutLimit: true,
    },
    enabled: isEventQuotesQueryEnabled ? serviceRenderedQuotesQuery.isSuccess : true,
    select(data) {
      return data.results;
    },
  });

  const quotableParts = useMemo(() => {
    if (quotablePartsQuery.isSuccess) {
      if (isEventQuotesQueryEnabled) {
        if (!serviceRenderedQuotesQuery.isSuccess) return [];
        // if event Id is passed, we need to filter out the quotes that are already selected

        const alreadySelectedQuotesIds = serviceRenderedQuotesQuery.data?.data.map(
          (q) => q.quoteLineId,
        );
        return quotablePartsQuery.data.filter(
          (quote) => !alreadySelectedQuotesIds.includes(quote.id),
        );
      }

      return quotablePartsQuery.data;
    }

    return [];
  }, [
    isEventQuotesQueryEnabled,
    quotablePartsQuery.data,
    quotablePartsQuery.isSuccess,
    serviceRenderedQuotesQuery.data?.data,
    serviceRenderedQuotesQuery.isSuccess,
  ]);

  const handleToggleOpen = useCallback(() => setOpen((open) => !open), [setOpen]);

  const handlePendingAddToUpdateExsting = useCallback(
    (updatedQuote: Quotable) => {
      const quotableIndex = pendingUpdateExistingQuotable.findIndex(
        (item) => item.quoteUsedId == updatedQuote.quoteUsedId,
      );

      if (quotableIndex !== -1) {
        setPendingUpdateExistingQuotable((prevQuotables) => {
          const updatedQuotables = [...prevQuotables];
          updatedQuotables[quotableIndex] = updatedQuote;

          return updatedQuotables;
        });
      } else {
        setPendingUpdateExistingQuotable((prevQuotables) => [...prevQuotables, updatedQuote]);
      }

      const updatedQuotableIndex = quotable.findIndex(
        (item) => item.quoteUsedId == updatedQuote.quoteUsedId,
      );

      if (updatedQuotableIndex !== -1) {
        setQuotable((prevQuotables) => {
          const updatedQuotables = [...prevQuotables];
          updatedQuotables[updatedQuotableIndex] = updatedQuote;

          return updatedQuotables;
        });
      }
      setEditingQuote(undefined);
    },

    [pendingUpdateExistingQuotable, quotable],
  );
  const handleToggleQuoteLineSelect = useCallback(
    (id: number) => (value: Value) => {
      setSelectedQuoteLineIds(
        value ? [...selectedQuoteLineIds, id] : selectedQuoteLineIds.filter((_id) => _id !== id),
      );
      const temp = {
        ...billable,
        [id]: {
          billable: true,
          quantity: 1,
          lmpc: false,
        },
      };
      if (!value) {
        delete temp[id];
      }
      setBillable(temp);
      setIsSelected(!!Object.keys(temp).length);
    },
    [selectedQuoteLineIds, billable],
  );
  const handleToggleBillable = useCallback(
    (id: number) => (value: Value) => {
      setBillable({
        ...billable,
        [id]: {
          billable: !!value,
          quantity: billable[id].quantity || 1,
          lmpc: false,
        },
      });
    },
    [billable],
  );
  const handleToggleBillableQuantity = useCallback(
    (id: number) => (value: Value) => {
      const temp = {
        ...billable,
        [id]: {
          billable: billable[id].billable,
          quantity: +value,
          lmpc: false,
        },
      };
      const cond = Object.values(temp).some((item) => item.quantity <= 0);
      setIsSelectedValid(!cond);
      setBillable(temp);
    },
    [billable],
  );
  const handleDeleteQuoteLine = useCallback(async () => {
    for (let i = 0; i < pendingDeleteQuotable.length; i++) {
      const temp = pendingDeleteQuotable[i];
      const quoteUsed = QuoteUsed.create();
      quoteUsed.id = temp.quoteUsedId;
      await QuoteUsedClientService.Delete(quoteUsed);
    }
    setPendingDeleteQuotable([]);
  }, [pendingDeleteQuotable]);
  const handleUpdateQuoteLine = useCallback(async () => {
    for (let i = 0; i < pendingUpdateExistingQuotable.length; i++) {
      const temp = pendingUpdateExistingQuotable[i];
      const quoteUsed = QuoteUsed.create();
      quoteUsed.id = temp.quoteUsedId;
      quoteUsed.quantity = temp.quantity;
      quoteUsed.lmpc = temp.isLmpc == true ? 1 : 0;
      quoteUsed.billable = temp.isBillable == true ? 1 : 0;

      quoteUsed.quotedPrice = temp.quotedPrice;
      quoteUsed.fieldMask = temp.fieldMask;
      quoteUsed.fieldMask = quoteUsed.fieldMask.map((field) =>
        field == 'IsBillable' ? 'Billable' : field,
      );
      quoteUsed.fieldMask = quoteUsed.fieldMask.map((field) =>
        field == 'IsLmpc' ? 'Lmpc' : field,
      );

      // excludeInvoiceDiscount lives on QuoteLine, not QuoteUsed — handle separately
      const hasExcludeFlag = quoteUsed.fieldMask.some(
        (f) => f === 'excludeInvoiceDiscount' || f === 'ExcludeInvoiceDiscount',
      );
      if (hasExcludeFlag && temp.quoteLineId) {
        const ql = QuoteLine.create();
        ql.id = temp.quoteLineId;
        ql.excludeInvoiceDiscount = temp.excludeInvoiceDiscount ? 1 : 0;
        ql.fieldMask = ['ExcludeInvoiceDiscount'];
        await QuoteLineClientService.Update(ql);
      }
      quoteUsed.fieldMask = quoteUsed.fieldMask.filter(
        (f) => f !== 'excludeInvoiceDiscount' && f !== 'ExcludeInvoiceDiscount',
      );

      await QuoteUsedClientService.Update(quoteUsed);
    }
    setPendingUpdateExistingQuotable([]);
  }, [pendingUpdateExistingQuotable]);
  const handleAddToPendingDelete = useCallback(
    async (pendingRemoveQuotable: Quotable) => {
      const tempQuotable = quotable.filter(
        (quote) => quote.quoteUsedId != pendingRemoveQuotable.quoteUsedId,
      );
      setQuotable(tempQuotable);
      const tempPendingDelete = pendingDeleteQuotable;
      tempPendingDelete.push(pendingRemoveQuotable);
      setPendingDeleteQuotable(tempPendingDelete);
    },

    [pendingDeleteQuotable, quotable],
  );
  const handleRemovePending = useCallback(
    async (pendingRemoveQuotable: Quotable) => {
      const tempSelected = selectedQuoteLineIds.filter(
        (quote) => quote != pendingRemoveQuotable.quoteLineId,
      );
      setSelectedQuoteLineIds(tempSelected);
      const newPendingList = pendingQuotable.filter(
        (quote) => quote.quoteLineId != pendingRemoveQuotable.quoteLineId,
      );
      setPendingQuotable(newPendingList);
      if (setPendingQuotableProp) {
        setPendingQuotableProp(newPendingList);
      }
    },
    [pendingQuotable, setPendingQuotableProp, selectedQuoteLineIds],
  );
  const handleRemoveNewPending = useCallback(
    async (pendingRemoveQuotable: Quotable) => {
      const tempSelected = selectedQuoteLineIds.filter(
        (quote) => quote != pendingRemoveQuotable.quoteLineId,
      );
      setSelectedQuoteLineIds(tempSelected);
      const newPendingList = pendingNewQuotable.filter(
        (quote) => quote.quoteLineId != pendingRemoveQuotable.quoteLineId,
      );
      setPendingNewQuotable(newPendingList);
      if (setPendingNewQuotableProp) {
        setPendingNewQuotableProp(newPendingList);
      }
    },
    [pendingNewQuotable, setPendingNewQuotableProp, selectedQuoteLineIds],
  );
  const handleAddQuotes = useCallback(async () => {
    const temp: Quotable[] = [];
    const existingIds = new Set(pendingQuotable.map((item) => item.quoteLineId));

    Object.keys(billable).forEach((id) => {
      const quote = quotablePartsQuery.data?.find(
        (q) => q.id == +id && selectedQuoteLineIds.includes(q.id),
      );

      if (quote && !existingIds.has(quote.id)) {
        temp.push(
          Quotable.create({
            quantity: billable[+id].quantity,
            isBillable: billable[+id].billable,
            isLmpc: billable[+id].lmpc,
            isActive: true,
            isFlatrate: true,
            quotedPrice: parseInt(quote.adjustment),
            id: quote.id,
            quoteLineId: quote.id,
            description: quote.description,
            excludeInvoiceDiscount: quote.excludeInvoiceDiscount === 1,
          }),
        );
      }
    });

    const cond = !!temp.length;
    setIsSelected(cond);
    if (cond) {
      const updatedQuotable = [...pendingQuotable, ...temp];
      setPendingQuotable(updatedQuotable);
      setPendingQuotableProp?.(updatedQuotable);
      setOpen(false);
    }
  }, [
    billable,
    quotablePartsQuery.data,
    selectedQuoteLineIds,
    pendingQuotable,
    setPendingQuotableProp,
  ]);

  const handleToggleNewQuotable = useCallback(() => setNewQuotable((isShown) => !isShown), []);

  const handleUpdatePendingQuotable = useCallback(
    async (editingPendingNew: Quotable) => {
      const temp = pendingQuotable;

      const quotableIndex = temp.findIndex(
        (item) => item.quoteLineId == editingPendingNew.quoteLineId,
      );

      if (quotableIndex !== -1) {
        setPendingQuotable((prevQuotables) => {
          const updatedQuotables = [...prevQuotables];
          updatedQuotables[quotableIndex] = editingPendingNew;
          setPendingQuotableProp?.(updatedQuotables);
          return updatedQuotables;
        });
      }

      setEditPendingQuote(undefined);
    },

    [pendingQuotable, setPendingQuotableProp],
  );

  const handleUpdatePendingNewQuotable = useCallback(
    async (editingPendingNew: Quotable) => {
      const temp = pendingNewQuotable;
      const quotableIndex = temp.findIndex(
        (item) => item.quoteLineId == editingPendingNew.quoteLineId,
      );

      if (quotableIndex !== -1) {
        setPendingNewQuotable((prevQuotables) => {
          const updatedQuotables = [...prevQuotables];
          updatedQuotables[quotableIndex] = editingPendingNew;
          setPendingNewQuotableProp?.(updatedQuotables);
          return updatedQuotables;
        });
      }

      setEditPendingNewQuote(undefined);
    },

    [pendingNewQuotable, setPendingNewQuotableProp],
  );

  const queryClient = useQueryClient();

  const handleSavePendingQuotable = useCallback(async () => {
    // TODO mutation
    const tempPendingQuotable = pendingQuotable;
    const tempPendingNewQuotable = pendingNewQuotable;
    if (pendingUpdateExistingQuotable.length > 0) {
      await handleUpdateQuoteLine();
    }
    if (pendingDeleteQuotable.length > 0) {
      await handleDeleteQuoteLine();
    }
    if (
      (tempPendingQuotable.length > 0 || tempPendingNewQuotable.length > 0) &&
      servicesRenderedId
    ) {
      for (let i = 0; i < tempPendingQuotable.length; i++) {
        const quotePart = tempPendingQuotable[i];
        const req = QuoteUsed.create();
        req.quoteLineId = quotePart.quoteLineId;
        req.quotedPrice = quotePart.quotedPrice;
        req.quantity = quotePart.quantity;
        req.servicesRenderedId = servicesRenderedId;
        req.billable = quotePart.isBillable == true ? 1 : 0;
        req.lmpc = quotePart.isLmpc == true ? 1 : 0;

        try {
          req.fieldMask = ['Billable', 'Lmpc', 'Quantity', 'QuotedPrice'];
          await QuoteUsedClientService.Create(req);
          if (req.billable == 0) {
            await QuoteUsedClientService.Update(QuoteUsed.create({ id: req.id, billable: 0 }));
          }
        } catch (err) {
          console.error(err);
        }
      }
      for (let i = 0; i < tempPendingNewQuotable.length; i++) {
        const quotePart = tempPendingNewQuotable[i];
        const req = QuoteUsed.create();
        const quotelineReq = QuoteLine.create();
        quotelineReq.description = quotePart.description;
        quotelineReq.adjustment = quotePart.quotedPrice.toString();
        quotelineReq.warranty = 2;
        quotelineReq.excludeInvoiceDiscount = quotePart.excludeInvoiceDiscount ? 1 : 0;
        const quotelineRes = await QuoteLineClientService.Create(quotelineReq);
        req.quoteLineId = quotelineRes!.id;
        req.quotedPrice = quotePart.quotedPrice;
        req.quantity = quotePart.quantity;
        req.lmpc = quotePart.isLmpc == true ? 1 : 0;
        req.servicesRenderedId = servicesRenderedId;
        req.billable = quotePart.isBillable == true ? 1 : 0;

        try {
          await QuoteUsedClientService.Create(req);
          if (req.billable == 0) {
            await QuoteUsedClientService.Update(QuoteUsed.create({ id: req.id, billable: 0 }));
          }
        } catch {
          console.log('failed to add quotable item');
        }
      }

      if (setPendingQuotableProp) {
        setPendingQuotableProp([]);
      }
      if (setPendingNewQuotableProp) {
        setPendingNewQuotableProp([]);
      }

      setSelectedQuoteLineIds([]);
    }

    setPendingNewQuotable([]);
    setPendingQuotable([]);
    if (onUpdate) {
      await onUpdate();
    }
    queryClient.invalidateQueries({
      queryKey: [
        queryKeys.eventQuotes.root,
        queryKeys.eventQuotes.list,
        queryKeys.eventQuotes.getHash({ servicesRenderedId }),
      ],
    });
  }, [
    pendingQuotable,
    pendingNewQuotable,
    pendingUpdateExistingQuotable.length,
    pendingDeleteQuotable.length,
    servicesRenderedId,
    onUpdate,
    queryClient,
    handleUpdateQuoteLine,
    handleDeleteQuoteLine,
    setPendingQuotableProp,
    setPendingNewQuotableProp,
  ]);

  const loading =
    (isEventQuotesQueryEnabled && serviceRenderedQuotesQuery.isFetching) ||
    quotablePartsQuery.isPending;

  const handleSaveNewQuotable = useCallback(
    (safeData: Quotable) => {
      const quantity = safeData.quantity;
      const billable = safeData.isBillable;
      const lmpc = safeData.isLmpc;

      const quotePart = Quotable.create();

      Object.assign(quotePart, {
        ...safeData,
        quantity,
        billable,
        lmpc,
        isActive: true, // TODO rest default props?
      });
      const newPendingNewQuotable = [...pendingNewQuotable, quotePart];
      setPendingNewQuotable(newPendingNewQuotable);
      if (setPendingNewQuotableProp) {
        setPendingNewQuotableProp(newPendingNewQuotable);
      }

      setNewQuotable(false);
      setOpen(false);
    },
    [pendingNewQuotable, setPendingNewQuotableProp],
  );

  const actions = useMemo(() => {
    let actions: ActionsProps = [];
    if (onUpdate) {
      actions = [
        ...actions,
        {
          label: 'Add',
          onClick: handleToggleOpen,
          disabled: loading || archived,
        },
      ];
    }
    if (onClose) {
      actions = [
        ...actions,
        {
          label: 'Close',
          onClick: onClose,
          disabled: loading,
        },
      ];
    }
    if (servicesRenderedId && servicesRenderedId !== 0 && onUpdate && !pendingNewQuotableProp) {
      actions = [
        ...actions,
        {
          label: 'Save Material Changes',
          onClick: handleSavePendingQuotable,
          disabled:
            loading ||
            archived ||
            (pendingQuotable.length === 0 &&
              pendingDeleteQuotable.length === 0 &&
              pendingNewQuotable.length === 0 &&
              pendingUpdateExistingQuotable.length === 0),
        },
      ];
    }
    return actions;
  }, [
    handleSavePendingQuotable,
    handleToggleOpen,
    archived,
    loading,
    onClose,
    onUpdate,
    pendingDeleteQuotable.length,
    pendingNewQuotable.length,
    pendingQuotable.length,
    pendingUpdateExistingQuotable.length,
    servicesRenderedId,
    pendingNewQuotableProp,
  ]);

  const data: Data = loading
    ? makeFakeRows(6, 20)
    : quotableParts
        .filter((quote) => {
          // Filter by description
          const matchesDescription = filter
            ? quote.description.toLocaleLowerCase().includes(filter.toLocaleLowerCase())
            : true;

          // Filter by department. A selected department shows only items actually
          // tagged with that department — generic (department 0 / "All Departments")
          // items are intentionally excluded so filtering respects the department strictly.
          const matchesDepartment =
            departmentFilter.length > 0
              ? quote.departments.split(',').some((dep) => departmentFilter.includes(Number(dep)))
              : true;

          return matchesDescription && matchesDepartment;
        })
        .map((quote) => [
          {
            value: (
              <Field
                name="selected"
                type="checkbox"
                style={{ marginBottom: 0 }}
                value={selectedQuoteLineIds.includes(quote.id)}
                onChange={handleToggleQuoteLineSelect(quote.id)}
              />
            ),
          },
          {
            value: selectedQuoteLineIds.includes(quote.id) ? (
              <Field
                name="selectedBillable"
                type="checkbox"
                style={{ marginBottom: 0 }}
                value={billable[quote.id].billable}
                onChange={handleToggleBillable(quote.id)}
              />
            ) : (
              ''
            ),
          },
          {
            value: quote.excludeInvoiceDiscount === 1 ? '✓' : '',
          },
          { value: quote.description },
          {
            value: selectedQuoteLineIds.includes(quote.id) ? (
              <Field
                name="selected"
                type="number"
                style={{ marginBottom: 0 }}
                value={billable[quote.id].quantity}
                onChange={handleToggleBillableQuantity(quote.id)}
              />
            ) : (
              ''
            ),
          },
          {
            value: usd(
              selectedQuoteLineIds.includes(quote.id)
                ? billable[quote.id].quantity * parseInt(quote.adjustment)
                : parseInt(quote.adjustment),
            ),
          },
        ]);
  const dataQuotable: Data = loading
    ? makeFakeRows(6, 5)
    : [
        ...pendingQuotable.map((quote) => [
          { value: <strong>{quote.description}</strong> },
          { value: <strong>{quote.quantity}</strong> },
          { value: <strong>{usd(quote.quotedPrice)}</strong> },
          {
            value: <strong>{usd(quote.quantity * quote.quotedPrice)}</strong>,
          },
          { value: <strong>{quote.isBillable ? 'Yes' : 'No'}</strong> },
          { value: <strong>{quote.excludeInvoiceDiscount ? 'Yes' : 'No'}</strong> },
          {
            value: (
              <div>
                <Tooltip key="delete" content="Delete">
                  <IconButton
                    key="deleteIcon"
                    size="small"
                    onClick={() => handleRemovePending(quote)}
                    disabled={archived}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip key="edit" content="Edit">
                  <IconButton
                    key="editIcon"
                    size="small"
                    onClick={() => setEditPendingQuote(quote)}
                    disabled={archived}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              </div>
            ),
          },
        ]),
        ...pendingNewQuotable.map((quote) => [
          { value: <strong>{quote.description}</strong> },
          { value: <strong>{quote.quantity}</strong> },
          { value: <strong>{usd(quote.quotedPrice)}</strong> },
          {
            value: <strong>{usd(quote.quantity * quote.quotedPrice)}</strong>,
          },
          { value: <strong>{quote.isBillable ? 'Yes' : 'No'}</strong> },
          { value: <strong>{quote.excludeInvoiceDiscount ? 'Yes' : 'No'}</strong> },
          {
            value: (
              <div>
                <Tooltip key="delete" content="Delete">
                  <IconButton
                    key="deleteIcon"
                    size="small"
                    onClick={() => handleRemoveNewPending(quote)}
                    disabled={archived}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip key="edit" content="Edit">
                  <IconButton
                    key="editIcon"
                    size="small"
                    onClick={() => setEditPendingNewQuote(quote)}
                    disabled={archived}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              </div>
            ),
          },
        ]),
        ...quotable.map((quote) => [
          { value: quote.description },
          { value: quote.quantity },
          { value: usd(quote.quotedPrice) },
          { value: usd(quote.quantity * quote.quotedPrice) },
          { value: quote.isBillable ? 'Yes' : 'No' },
          { value: quote.excludeInvoiceDiscount ? 'Yes' : 'No' },
          {
            value: (
              <div>
                <Tooltip key="delete" content="Delete">
                  <IconButton
                    key="deleteIcon"
                    size="small"
                    disabled={archived}
                    onClick={() => handleAddToPendingDelete(quote)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip key="edit" content="Edit">
                  <IconButton
                    key="editIcon"
                    size="small"
                    onClick={() => setEditingQuote(quote)}
                    disabled={archived}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              </div>
            ),
          },
        ]),
      ];

  // Combined filter handler for both description and departments
  const handleFilterChange = useCallback(
    (params: { description: string; departments?: number[] }) => {
      setFilter(params.description);
      if (params.departments) {
        setDepartmentFilter(params.departments);
      }
    },
    [],
  );

  return (
    <div>
      <SectionBar title="Supplies / Services" actions={actions} fixedActions />

      {/* Render New Part/Labor inline at the top */}
      {newQuotable && (
        <div
          className="quote-selector-new-quotable"
          style={{
            marginBottom: '20px',
            border: '1px solid #e0e0e0',
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            padding: '16px',
            backgroundColor: '#fff',
            animation: 'quoteSelector-fadeIn 0.3s ease-in-out',
            zIndex: 5,
            position: 'relative',
          }}
        >
          <Form<Quotable>
            title="Adding Part/Service To Current Job"
            schema={createQuotableSchema()}
            onClose={handleToggleNewQuotable}
            onSave={handleSaveNewQuotable}
            data={Quotable.create({ isBillable: true, quantity: 1 })}
          />
        </div>
      )}

      <InfoTable
        styles={{ minWidth: '600px' }}
        columns={COLUMNS_QUOTABLE}
        data={dataQuotable}
        loading={loading}
      />

      {/* Divider when any inline content is shown */}
      {(open || newQuotable) && (
        <div
          style={{
            margin: '20px 0',
            borderTop: '1px solid #e0e0e0',
            position: 'relative',
            height: '20px',
          }}
        >
          <span
            style={{
              position: 'absolute',
              top: '-10px',
              left: '20px',
              padding: '0 10px',
              backgroundColor: 'white',
              color: '#666',
              fontSize: '12px',
              fontWeight: 'bold',
              textTransform: 'uppercase',
            }}
          >
            Add Materials
          </span>
        </div>
      )}

      {open && (
        <div
          className="quote-selector-inline-content"
          style={{
            marginTop: '10px',
            border: '1px solid #e0e0e0',
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            overflow: 'hidden',
            animation: 'quoteSelector-fadeIn 0.3s ease-in-out',
          }}
        >
          <style>
            {`
              @keyframes quoteSelector-fadeIn {
                from { opacity: 0; transform: translateY(-10px); }
                to { opacity: 1; transform: translateY(0); }
              }
            `}
          </style>
          <SectionBar
            title="Select Item(s)"
            actions={[
              {
                label: 'New Part/Labor',
                variant: 'outlined',
                onClick: handleToggleNewQuotable,
              },
              {
                label: 'Add Selected',
                onClick: handleAddQuotes,
                disabled: !isSelected || !isSelectedValid,
              },
              { label: 'Close', onClick: handleToggleOpen },
            ]}
            fixedActions
          />
          <EnhancedFilter
            onSearch={handleFilterChange}
            initialFilters={{
              description: filter,
              departments: departmentFilter,
            }}
          />
          <InfoTable
            className={clsx('services-table', {
              error: !isSelectedValid,
            })}
            columns={COLUMNS}
            data={data}
            hoverable
            loading={quotablePartsQuery.isPending}
          />
        </div>
      )}

      {editingQuote && (
        <Modal open onClose={() => setEditingQuote(undefined)}>
          <Form<Quotable>
            title="Edit Existing "
            schema={createQuotableSchema(editingQuote)}
            onClose={() => setEditingQuote(undefined)}
            onSave={handlePendingAddToUpdateExsting}
            data={editingQuote}
          />
        </Modal>
      )}
      {editPendingQuote && (
        <Modal open onClose={() => setEditPendingQuote(undefined)}>
          <Form<Quotable>
            title="Edit Pending Quote "
            schema={createQuotableSchema(editPendingQuote)}
            onClose={() => setEditPendingQuote(undefined)}
            onSave={handleUpdatePendingQuotable}
            data={editPendingQuote}
          />
        </Modal>
      )}
      {editPendingNewQuote && (
        <Modal open onClose={() => setEditPendingNewQuote(undefined)}>
          <Form<Quotable>
            title="Edit Pending Quote "
            schema={createQuotableSchema(editPendingNewQuote)}
            onClose={() => setEditPendingNewQuote(undefined)}
            onSave={handleUpdatePendingNewQuotable}
            data={editPendingNewQuote}
          />
        </Modal>
      )}
    </div>
  );
};
